import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { userContract } from "@common/contracts/user.contract";
import {
    UserProductTipKey,
    UserProductTipsResponse,
    UserSchema,
    UserStatusResponseSchema,
    userSchema,
    userProductTipsResponseSchema,
    userStatusResponseSchema,
    updateUserBodySchema,
    userMetaSchema,
    updateUserMetaBodySchema,
} from "@common/schemas/user.schema";
import {
    UserEntitiesResponseDto,
    UserEntitiesResponse,
} from "@common/dto/onboarding/user-entities-response.dto";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useUpdateUser } from "@hooks/useUpdateUser";
import { useUser } from "@hooks/useUser";

const userApiClient = getBackendAPIClient(userContract);
export const userStatusQueryKey = "status" as const;

export async function getUserStatus(): Promise<UserStatusResponseSchema | void> {
    try {
        const response = await userApiClient.getStatus();
        return userStatusResponseSchema.parse(response.body);
    } catch {
        // ignore error
    }
}

interface SetUserProductTipPayload {
    key: UserProductTipKey;
    value: string;
}

interface SetUserProductTipMutationContext {
    previousStatus?: UserStatusResponseSchema;
}

export function useSetUserProductTipMutation() {
    const queryClient = useQueryClient();

    return useMutation<
        UserProductTipsResponse,
        unknown,
        SetUserProductTipPayload,
        SetUserProductTipMutationContext
    >({
        mutationFn: async (payload) => {
            const response = await userApiClient.setProductTip({
                body: payload,
            });
            return userProductTipsResponseSchema.parse(response.body);
        },
        onMutate: async ({ key, value }) => {
            await queryClient.cancelQueries({
                queryKey: [userStatusQueryKey],
            });

            const previousStatus = queryClient.getQueryData<
                UserStatusResponseSchema | undefined
            >([userStatusQueryKey]);

            if (!previousStatus) {
                return { previousStatus };
            }

            queryClient.setQueryData(
                [userStatusQueryKey],
                (previous: UserStatusResponseSchema | undefined) => {
                    if (!previous) {
                        return previous;
                    }

                    return {
                        ...previous,
                        user: {
                            ...previous.user,
                            productTips: {
                                ...(previous.user.productTips ?? {}),
                                [key]: value,
                            },
                        },
                    };
                },
            );

            return { previousStatus };
        },
        onError: (_error, _payload, context) => {
            if (context?.previousStatus) {
                queryClient.setQueryData(
                    [userStatusQueryKey],
                    context.previousStatus,
                );
            }
        },
        onSuccess: (productTips) => {
            queryClient.setQueryData(
                [userStatusQueryKey],
                (previous: UserStatusResponseSchema | undefined) => {
                    if (!previous) {
                        return previous;
                    }

                    return {
                        ...previous,
                        user: {
                            ...previous.user,
                            productTips,
                        },
                    };
                },
            );
        },
    });
}

export async function getUserEntities(
    workspaceId: string,
): Promise<UserEntitiesResponse | void> {
    try {
        const response = await userApiClient.getEntities({
            params: { workspaceId },
        });
        return UserEntitiesResponseDto.deserialize(response.body);
    } catch (error) {
        if (error && typeof error === "object" && "status" in error) {
            if ((error as { status: number }).status === 404) {
                throw new Error("Team not found");
            }
        }
        // ignore other errors
    }
}

export function useCreateAvatarUploadUrlMutation() {
    return useMutation({
        mutationFn: async (filename: string) => {
            const result = await userApiClient.createAvatarUploadUrl({
                body: { name: filename },
            });
            return result.body;
        },
    });
}

export function useUpdateAvatarMutation() {
    const updateUser = useUpdateUser();

    return useMutation({
        mutationFn: async (avatarPath: string | null) => {
            const result = await userApiClient.updateAvatar({
                body: { avatarPath },
            });
            return result.body;
        },
        onSuccess: (updatedUser) => {
            updateUser(updatedUser);
        },
    });
}

type UpdateUserPayload = z.infer<typeof updateUserBodySchema>;

export function useUpdateUserMutation() {
    const user = useUser();
    const updateUser = useUpdateUser();

    return useMutation<UserSchema, unknown, UpdateUserPayload, UserSchema>({
        mutationFn: async (payload: UpdateUserPayload) => {
            const result = await userApiClient.update({ body: payload });
            return userSchema.parse(result.body);
        },
        onMutate: (payload) => {
            updateUser({
                ...user,
                ...payload,
                ...(payload.preferredName
                    ? { name: payload.preferredName }
                    : {}),
            });
            return user;
        },
        onError: (_1, _2, previous) => {
            if (previous) {
                updateUser(previous);
            }
        },
        onSuccess: (updatedUser) => {
            const parsedUser = userSchema.parse(updatedUser);
            updateUser(parsedUser);
        },
    });
}

export const userMetaQueryKey = "userMeta" as const;

export function useUserMetaQuery() {
    return useQuery({
        queryKey: [userMetaQueryKey],
        queryFn: async () => {
            const result = await userApiClient.getMeta();
            return userMetaSchema.parse(result.body);
        },
        staleTime: 15 * 60 * 1000, // 15 minutes
        refetchOnWindowFocus: false,
    });
}

type UpdateUserMetaPayload = z.infer<typeof updateUserMetaBodySchema>;

export function useUpdateUserMetaMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: UpdateUserMetaPayload) => {
            const result = await userApiClient.updateMeta({ body: payload });
            return userMetaSchema.parse(result.body);
        },
        onSuccess: (updatedMeta) => {
            queryClient.setQueryData([userMetaQueryKey], updatedMeta);
        },
    });
}
